/**
 * Primitivas compartidas del panel (decisión §4.4). Usan solo los colores adm-*
 * de src/index.css, que existen dentro de un elemento .admin (§4.2).
 * Las pantallas no repiten estilos: usan estas.
 */
import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, CircleAlert, CircleCheck, LoaderCircle, TriangleAlert, X } from "lucide-react";

type IconComponent = React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Ícono de la maqueta: 16 px, trazo 1,75. Uso: <Menu {...ICON} /> */
export const ICON = { size: 16, strokeWidth: 1.75 } as const;

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return <LoaderCircle size={size} strokeWidth={1.75} className={cx("shrink-0 animate-spin", className)} aria-hidden="true" />;
}

/** Texto secundario dentro de un Alert («Lo que ves puede estar desactualizado.») */
export function Muted({ children }: { children?: React.ReactNode }) {
  return <span className="text-adm-t55">{children}</span>;
}

/* ---------------------------------------------------------------- Button */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap border font-medium transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50";

const BUTTON_SIZE = {
  md: "h-10 rounded-[10px] px-4 text-sm max-md:h-11",
  sm: "h-8 rounded-lg px-3 text-[13px] max-md:h-10",
};

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: "border-adm-acento bg-adm-acento text-adm-sobre-acento hover:border-adm-acento-hover hover:bg-adm-acento-hover",
  secondary: "border-adm-border-2 bg-adm-surface text-adm-text hover:bg-adm-card",
  ghost: "border-transparent bg-transparent text-adm-t55 hover:bg-adm-t5 hover:text-adm-text",
  danger: "border-adm-rojo bg-adm-rojo text-white adm-dark:text-[#1a0604]",
};

/** .btn-ghost.danger de la maqueta («Limpiar mes») */
const GHOST_DANGER = "border-transparent bg-transparent text-adm-rojo hover:bg-adm-rojo-suave";

type ButtonProps = {
  variant?: ButtonVariant;
  size?: "md" | "sm";
  /** solo con variant="ghost": texto rojo, como «Limpiar mes» */
  tone?: "danger";
  block?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  /** muestra el spinner en lugar del ícono y deshabilita el botón */
  loading?: boolean;
  /** si viene, se dibuja un <a> con el mismo aspecto */
  href?: string;
  target?: string;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
};

export function Button({
  variant = "secondary",
  size = "md",
  tone,
  block,
  icon,
  iconRight,
  loading,
  href,
  target,
  className,
  children,
  type,
  disabled,
  ...rest
}: ButtonProps) {
  const classes = cx(
    BUTTON_BASE,
    BUTTON_SIZE[size],
    variant === "ghost" && tone === "danger" ? GHOST_DANGER : BUTTON_VARIANT[variant],
    block && "w-full",
    className,
  );
  const content = (
    <>
      {loading ? <Spinner /> : icon}
      {children}
      {iconRight}
    </>
  );
  if (href) {
    return (
      <a href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button type={type ?? "button"} disabled={disabled || loading} aria-busy={loading || undefined} className={classes} {...rest}>
      {content}
    </button>
  );
}

/* ------------------------------------------------------------ IconButton */

type IconButtonProps = {
  /** texto para lectores de pantalla y tooltip; obligatorio */
  label: string;
  tone?: "danger";
  bordered?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
};

export function IconButton({ label, tone, bordered, className, children, type, ...rest }: IconButtonProps) {
  return (
    <button
      type={type ?? "button"}
      aria-label={label}
      title={label}
      className={cx(
        "inline-grid size-9 shrink-0 place-items-center rounded-[10px] text-adm-t55 transition-colors cursor-pointer hover:bg-adm-t5 hover:text-adm-text disabled:cursor-not-allowed disabled:opacity-50 max-md:size-11",
        bordered ? "border border-adm-border-2 bg-adm-surface" : "border-0 bg-transparent",
        tone === "danger" && "hover:bg-adm-rojo-suave hover:text-adm-rojo",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ Card */

export function Card({ soft, className, children, ...rest }: { soft?: boolean; className?: string; children?: React.ReactNode; [key: string]: any }) {
  return (
    <div className={cx("rounded-[18px] border border-adm-border", soft ? "bg-adm-card" : "bg-adm-surface", className)} {...rest}>
      {children}
    </div>
  );
}

/* ----------------------------------------------------------------- Badge */

export type BadgeTone = "ok" | "amber" | "red" | "neutral";

const BADGE_TONE: Record<BadgeTone, string> = {
  neutral: "bg-adm-t10 text-adm-t70",
  ok: "bg-adm-acento-suave text-adm-acento-texto",
  amber: "bg-adm-ambar-suave text-adm-ambar",
  red: "bg-adm-rojo-suave text-adm-rojo",
};

export function Badge({ tone = "neutral", icon: Icon, className, children }: { tone?: BadgeTone; icon?: IconComponent; className?: string; children?: React.ReactNode }) {
  return (
    <span className={cx("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium", BADGE_TONE[tone], className)}>
      {Icon && <Icon size={12} strokeWidth={1.75} />}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------- Seg */

export function Seg<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  /** nombre del grupo para lectores de pantalla */
  label?: string;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cx(
        "inline-flex gap-0.5 rounded-xl border border-adm-border bg-adm-card p-[3px] max-md:flex max-md:w-full max-md:overflow-x-auto",
        className,
      )}
    >
      {options.map((opt) => {
        const on = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(opt.value)}
            className={cx(
              "h-8 whitespace-nowrap rounded-[9px] px-3.5 text-[13px] font-medium transition-colors cursor-pointer max-md:h-10 max-md:flex-1",
              on
                ? "bg-adm-surface text-adm-text shadow-[0_0_0_1px_var(--border),0_1px_2px_rgba(0,0,0,.05)]"
                : "bg-transparent text-adm-t55 hover:text-adm-text",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ----------------------------------------------------------------- Field */

export function Field({
  label,
  htmlFor,
  hint,
  className,
  children,
}: {
  label: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={cx("flex flex-col gap-1.5", className)}>
      <label htmlFor={htmlFor} className="text-[13px] font-medium text-adm-t70">
        {label}
      </label>
      {children}
      {hint && <span className="text-xs text-adm-t55">{hint}</span>}
    </div>
  );
}

/* --------------------------------------------------------- Input, Select */

const CONTROL =
  "h-[42px] w-full rounded-[10px] border border-adm-border-2 bg-adm-surface px-3 text-sm text-adm-text outline-none transition-[border-color,box-shadow] placeholder:text-adm-t40 focus:border-adm-acento focus:shadow-[0_0_0_3px_var(--acento-suave)] disabled:cursor-not-allowed disabled:opacity-60 max-md:h-11";

export function Input({ mono, className, ...rest }: { mono?: boolean; className?: string; [key: string]: any }) {
  return <input className={cx(CONTROL, mono && "font-mono text-xs", className)} {...rest} />;
}

export function Select({ className, children, ...rest }: { className?: string; children?: React.ReactNode; [key: string]: any }) {
  return (
    <div className="relative">
      <select className={cx(CONTROL, "cursor-pointer appearance-none pr-9", className)} {...rest}>
        {children}
      </select>
      <ChevronDown size={16} strokeWidth={2} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-adm-t40" aria-hidden="true" />
    </div>
  );
}

/* ---------------------------------------------------------------- Toggle */

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: React.ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        "inline-flex min-h-10 items-center gap-2.5 bg-transparent text-left text-sm text-adm-t70 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-11",
        className,
      )}
    >
      <span className={cx("relative h-5 w-[34px] shrink-0 rounded-full transition-colors", checked ? "bg-adm-acento" : "bg-adm-border-2")}>
        <span
          className={cx(
            "absolute left-0.5 top-0.5 size-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.2)] transition-transform",
            checked && "translate-x-[14px]",
          )}
        />
      </span>
      {label}
    </button>
  );
}

/* ----------------------------------------------------------------- Empty */

export function Empty({
  icon: Icon,
  title,
  children,
  action,
  size = "md",
  className,
}: {
  icon?: IconComponent;
  title: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  size?: "md" | "sm";
  className?: string;
}) {
  return (
    <div
      className={cx(
        "flex flex-col items-center gap-2.5 border border-dashed border-adm-border-2 bg-adm-surface text-center",
        size === "sm" ? "rounded-[14px] px-4 py-6" : "rounded-[18px] px-6 py-14",
        className,
      )}
    >
      {Icon && <Icon size={32} strokeWidth={1.5} className="text-adm-t40" />}
      <div className="text-[15px] font-medium">{title}</div>
      {children && <p className="max-w-[380px] text-sm text-adm-t55">{children}</p>}
      {action}
    </div>
  );
}

/* ----------------------------------------------------------------- Alert */

export function Alert({
  tone,
  icon,
  action,
  className,
  children,
}: {
  tone: "amber" | "red";
  icon?: IconComponent;
  action?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) {
  const Icon = icon ?? (tone === "red" ? CircleAlert : TriangleAlert);
  return (
    <div
      role={tone === "red" ? "alert" : "status"}
      className={cx(
        "flex items-center gap-3 rounded-xl border py-3 pl-4 pr-3.5 text-sm leading-normal",
        tone === "red" ? "border-adm-rojo-borde bg-adm-rojo-suave" : "border-adm-ambar-borde bg-adm-ambar-suave",
        className,
      )}
    >
      <Icon {...ICON} className={tone === "red" ? "text-adm-rojo" : "text-adm-ambar"} />
      <div className="min-w-0 flex-1">{children}</div>
      {action}
    </div>
  );
}

/* -------------------------------------------------------------- Skeleton */

export function Skeleton({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <div aria-hidden="true" className={cx("animate-pulse rounded-[14px] bg-adm-card", className)} style={style} />;
}

/* ------------------------------------------------- Drawer y Modal (§1.8) */

/** Esc cierra, el foco entra al abrir y vuelve adonde estaba al cerrar. */
function useDialog(open: boolean, onClose: () => void) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => {
      const target = panelRef.current?.querySelector<HTMLElement>("[data-autofocus]") ?? panelRef.current;
      target?.focus();
    }, 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(focusTimer);
      previous?.focus?.();
    };
  }, [open]);

  return panelRef;
}

/** Cajón lateral derecho (.drawer de la maqueta). Poné data-autofocus en el campo que tiene que recibir el foco. */
export function Drawer({
  open,
  onClose,
  eyebrow,
  title,
  subtitle,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const panelRef = useDialog(open, onClose);
  const titleId = useId();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[110]">
      <div className="absolute inset-0 bg-adm-overlay backdrop-blur-[3px]" onClick={onClose} aria-hidden="true" />
      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="absolute inset-y-0 right-0 flex w-[420px] max-w-full flex-col border-l border-adm-border bg-adm-surface shadow-adm outline-none max-md:w-full"
      >
        <div className="flex items-start justify-between gap-3 border-b border-adm-border pb-4 pl-6 pr-4 pt-[18px]">
          <div>
            {eyebrow && <span className="text-xs font-medium text-adm-acento-texto">{eyebrow}</span>}
            <div id={titleId} className="text-lg font-semibold tracking-[-.01em]">
              {title}
            </div>
            {subtitle && <div className="text-[13px] leading-[1.55] text-adm-t55">{subtitle}</div>}
          </div>
          <IconButton label="Cerrar" onClick={onClose}>
            <X {...ICON} />
          </IconButton>
        </div>
        <div className="flex flex-1 flex-col gap-[22px] overflow-auto px-6 py-5">{children}</div>
        {footer && <div className="flex items-center gap-2.5 border-t border-adm-border px-6 py-3.5">{footer}</div>}
      </aside>
    </div>
  );
}

/** Ventana centrada (.modal de la maqueta). size="sm" = 420 px (confirmaciones). */
export function Modal({
  open,
  onClose,
  eyebrow,
  title,
  footer,
  size = "md",
  showClose = true,
  children,
}: {
  open: boolean;
  onClose: () => void;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  footer?: React.ReactNode;
  size?: "md" | "sm";
  showClose?: boolean;
  children?: React.ReactNode;
}) {
  const panelRef = useDialog(open, onClose);
  const titleId = useId();
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-adm-overlay p-4 backdrop-blur-[3px]"
      onMouseDown={(e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cx(
          "flex max-h-[calc(100vh-32px)] w-full flex-col rounded-[20px] border border-adm-border bg-adm-surface shadow-adm outline-none",
          size === "sm" ? "max-w-[420px]" : "max-w-[500px]",
        )}
      >
        <div className="flex items-start justify-between gap-3 pl-6 pr-4 pt-[22px]">
          <div>
            {eyebrow && <span className="text-xs font-medium text-adm-acento-texto">{eyebrow}</span>}
            <div id={titleId} className="text-lg font-semibold tracking-[-.01em]">
              {title}
            </div>
          </div>
          {showClose && (
            <IconButton label="Cerrar" onClick={onClose}>
              <X {...ICON} />
            </IconButton>
          )}
        </div>
        <div className="flex flex-col gap-4 overflow-auto px-6 py-[18px]">{children}</div>
        {footer && <div className="flex gap-2.5 px-6 pb-6 pt-1 [&>*]:flex-1">{footer}</div>}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Toast */

type ToastKind = "ok" | "error";
type ToastApi = { show: (message: string, kind?: ToastKind) => void };

const ToastContext = createContext<ToastApi | null>(null);

/** Va una sola vez, dentro del div .admin (lo monta la Ficha 3). */
export function ToastProvider({ children }: { children?: React.ReactNode }) {
  const [toast, setToast] = useState<{ id: number; message: string; kind: ToastKind } | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback((message: string, kind: ToastKind = "ok") => {
    window.clearTimeout(timer.current);
    setToast({ id: Date.now(), message, kind });
    // los errores quedan más tiempo para que se alcancen a leer (§1.8, P-13)
    timer.current = window.setTimeout(() => setToast(null), kind === "error" ? 6000 : 2800);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div aria-live="polite" role="status" className="pointer-events-none fixed inset-x-4 bottom-4 z-[200] flex justify-center md:inset-x-0 md:bottom-7">
        {toast && (
          <div key={toast.id} className="pointer-events-auto flex items-center gap-2 rounded-xl bg-adm-text px-4 py-2.5 text-sm text-adm-bg shadow-adm max-md:w-full">
            {toast.kind === "error" ? (
              <CircleAlert {...ICON} className="text-adm-rojo" />
            ) : (
              <CircleCheck {...ICON} className="text-adm-acento adm-dark:text-[#2f6f4f]" />
            )}
            <span>{toast.message}</span>
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

/** const toast = useToast(); toast.show("Portada publicada"); toast.show("No se pudo guardar", "error"); */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast() se usó fuera de <ToastProvider>");
  return api;
}
